import React from 'react';

import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { SalesDetailsHelper } from '../../../../helpers/sales-details.helper';
import {
  ISalesDetails,
  ISalesDetailsPayment,
  ISalesDetailsPaymentState,
} from '../../../../types/sales-details.type';
import { ArrayUtils } from '../../../../utils/array.utils';
import { NumberUtils } from '../../../../utils/number.utils';

interface IProps {
  details: ISalesDetails[];
}

const SalesDetailsTotal = React.memo(({ details }: IProps) => {
  const { t } = useTranslation();

  const payments = React.useMemo(
    () =>
      details
        .map((detail) => detail.payments ?? [])
        .reduce((prev, items) => [...prev, ...items], []),
    [details],
  );

  const invoice = React.useMemo(() => {
    const total = ArrayUtils.calculateTotal<ISalesDetails>(details, 'total');

    return NumberUtils.toCurrency(total);
  }, [details]);

  const paid = React.useMemo(() => {
    const filtered = payments.filter(
      (payment) => payment.state === ISalesDetailsPaymentState.completed,
    );

    const total = ArrayUtils.calculateTotal<ISalesDetailsPayment>(filtered, 'amount');

    return NumberUtils.toCurrency(total);
  }, [payments]);

  const waiting = React.useMemo(() => {
    const filtered = payments.filter(
      (payment) => payment.state !== ISalesDetailsPaymentState.completed,
    );

    const total = ArrayUtils.calculateTotal<ISalesDetailsPayment>(filtered, 'amount');

    return NumberUtils.toCurrency(total);
  }, [payments]);

  const failed = React.useMemo(() => {
    const filtered = payments.filter((payment) => SalesDetailsHelper.isFailed(payment));

    const total = ArrayUtils.calculateTotal<ISalesDetailsPayment>(filtered, 'amount');

    return NumberUtils.toCurrency(total);
  }, [payments]);

  return (
    <Grid container mt={3} spacing={3}>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table aria-label="sticky table">
            <TableBody>
              <TableRow>
                <TableCell>{t('sales_details.total.invoice')}</TableCell>
                <TableCell>{invoice}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell>{t('sales_details.total.paid')}</TableCell>
                <TableCell>{paid}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell>{t('sales_details.total.waiting_payments')}</TableCell>
                <TableCell>{waiting}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell>{t('sales_details.total.failed_payments')}</TableCell>
                <TableCell>{failed}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
});

export default SalesDetailsTotal;
